import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaTimes, FaCalendarAlt, FaCar, FaMapMarkerAlt, FaArrowsAltH, FaColumns, FaEye } from 'react-icons/fa';
import { AnimatePresence } from 'framer-motion';
import pb from '../pocketbase';
import ComparisonSlider from '../components/ComparisonSlider';
import Lightbox from '../components/Lightbox';
import './ProjectDetailsPage.css';

const getFileUrl = (record, filename) => {
    if (!filename) return "";
    return pb.files.getUrl(record, filename);
};

const ProjectDetailsPage = () => {
    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [similarProjects, setSimilarProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeImage, setActiveImage] = useState(0);
    const [galleryState, setGalleryState] = useState('apres'); // 'avant' or 'apres'

    // Lightbox State
    const [lightbox, setLightbox] = useState({
        isOpen: false,
        images: [],
        index: 0
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        const loadData = async () => {
            try {
                const record = await pb.collection('realisations').getOne(projectId);
                setProject(record);

                const similar = await pb.collection('realisations').getList(1, 4, {
                    filter: `id != "${projectId}" && categorie = "${record.categorie}"`,
                    sort: '-created'
                });
                setSimilarProjects(similar.items);
            } catch (err) {
                console.error("Fetch error", err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [projectId]);

    if (loading) return <div className="loading-state">Analyse du chantier...</div>;
    if (!project) return <div className="error-state">Projet non trouvé.</div>;

    const avantPhotos = Array.isArray(project.photo_avant) ? project.photo_avant : (project.photo_avant ? [project.photo_avant] : []);
    const apresPhotos = Array.isArray(project.photo_apres) ? project.photo_apres : (project.photo_apres ? [project.photo_apres] : []);
    const maxPairs = Math.max(avantPhotos.length, apresPhotos.length);

    const avantTexts = (project.liste_avant || "").split('\n').filter(Boolean);
    const apresTexts = (project.interventions || project.liste_apres || "").split('\n').filter(Boolean);

    // Lightbox handlers
    const openLightbox = (imgArray, index) => {
        const urls = imgArray.map(img => getFileUrl(project, img));
        setLightbox({
            isOpen: true,
            images: urls,
            index: index
        });
    };

    return (
        <div className="project-view-container">
            {/* Banner Section */}
            <section className="standard-banner-section">
                <div className="standard-banner-bg">
                    <img src="https://images.unsplash.com/photo-1601362840469-51e4d8d59085?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80" alt="Détails du projet" />
                    <div className="standard-banner-overlay"></div>
                </div>
                <div className="container standard-banner-content">
                    <div className="standard-title-box">
                        <h1>RÉALISATION</h1>
                    </div>
                </div>
            </section>

            <div className="container project-details-main">
                <div className="project-breadcrumb">
                    <Link to="/avant-apres"><FaArrowLeft /> Retour à la galerie</Link>
                    <span>/</span>
                    <span className="current">{project.titre}</span>
                </div>

                <div className="project-main-grid">
                    {/* LEFT: VISUALS */}
                    <div className="project-visuals-col">

                        {/* 1. TOP SECTION: THE INTERACTIVE SLIDER */}
                        <div className="main-comparison-box-premium">
                            {Math.min(avantPhotos.length, apresPhotos.length) > 0 ? (
                                <ComparisonSlider
                                    before={getFileUrl(project, avantPhotos[activeImage])}
                                    after={getFileUrl(project, apresPhotos[activeImage])}
                                />
                            ) : (
                                <div className="no-comparison">
                                    <img src={getFileUrl(project, apresPhotos[activeImage] || avantPhotos[activeImage])} alt="Project" style={{ width: '100%', borderRadius: '25px' }} />
                                </div>
                            )}
                        </div>

                        {maxPairs > 1 && (
                            <div className="project-gallery-strip-premium">
                                {(apresPhotos.length > 0 ? apresPhotos : avantPhotos).slice(0, maxPairs).map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`gallery-thumb-premium ${activeImage === idx ? 'selected' : ''}`}
                                        onClick={() => setActiveImage(idx)}
                                    >
                                        <img src={getFileUrl(project, (apresPhotos[idx] || avantPhotos[idx]))} alt="Thumbnail" />
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* 2. SECTION: THE GREEN/GRAY TOGGLE GALLERY */}
                        <div className="toggle-gallery-section-premium">
                            <div className="gallery-header-premium">
                                <div className="gallery-state-switcher-premium">
                                    <button
                                        className={`state-btn-premium avant ${galleryState === 'avant' ? 'active' : ''}`}
                                        onClick={() => setGalleryState('avant')}
                                    >
                                        AVANT
                                    </button>
                                    <button
                                        className={`state-btn-premium apres ${galleryState === 'apres' ? 'active' : ''}`}
                                        onClick={() => setGalleryState('apres')}
                                    >
                                        APRÈS
                                    </button>
                                </div>
                            </div>

                            <div className="active-gallery-grid-premium">
                                {(galleryState === 'avant' ? avantPhotos : apresPhotos).map((img, i) => (
                                    <div
                                        key={i}
                                        className="gallery-img-item-premium"
                                        onClick={() => openLightbox(galleryState === 'avant' ? avantPhotos : apresPhotos, i)}
                                    >
                                        <img src={getFileUrl(project, img)} alt={`${galleryState} ${i}`} />
                                    </div>
                                ))}
                                {(galleryState === 'avant' ? avantPhotos : apresPhotos).length === 0 && (
                                    <p style={{ textAlign: 'center', gridColumn: '1/-1', color: 'var(--text-tertiary)' }}>Aucune photo dans cette catégorie.</p>
                                )}
                            </div>
                        </div>

                        {/* 3. DETAILED TECHNICAL REPORT SECTION */}
                        <div className="project-detailed-report-premium">
                            <h2>📸 Rapport d'Intervention</h2>
                            <p className="report-intro-premium">Découvrez le détail complet de notre travail sur ce projet de {project.categorie === 'auto' ? 'rénovation automobile' : 'shampouinage de mobilier'}. Chaque zone a été traitée avec une attention particulière pour un résultat RoulezPropre impeccable.</p>

                            <div className="technical-comparison-list-premium">
                                {Array.from({ length: maxPairs }).map((_, idx) => (
                                    <div key={idx} className="comparison-card-premium">
                                        <span className="view-label-premium">Espace {idx + 1}</span>
                                        <div className="comparison-visual-grid-premium">
                                            <div
                                                className="premium-img-box"
                                                onClick={() => avantPhotos[idx] && openLightbox(avantPhotos, idx)}
                                                style={{ cursor: avantPhotos[idx] ? 'zoom-in' : 'default' }}
                                            >
                                                {avantPhotos[idx] ? (
                                                    <img src={getFileUrl(project, avantPhotos[idx])} alt="Avant" />
                                                ) : (
                                                    <div className="placeholder-img" style={{ height: '100%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Image non disponible</div>
                                                )}
                                                <span className="status-badge-premium avant">ÉNONCÉ DU PROBLÈME</span>
                                            </div>
                                            <div
                                                className="premium-img-box"
                                                onClick={() => apresPhotos[idx] && openLightbox(apresPhotos, idx)}
                                                style={{ cursor: apresPhotos[idx] ? 'zoom-in' : 'default' }}
                                            >
                                                {apresPhotos[idx] ? (
                                                    <img src={getFileUrl(project, apresPhotos[idx])} alt="Après" />
                                                ) : (
                                                    <div className="placeholder-img" style={{ height: '100%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Image non disponible</div>
                                                )}
                                                <span className="status-badge-premium apres">SOLUTION APPORTÉE</span>
                                            </div>
                                        </div>

                                        {/* Description sync */}
                                        <div className="comparison-descriptions-premium">
                                            <div className="description-item-premium problem">
                                                <h5><FaTimes /> Problématique</h5>
                                                <p>{avantTexts[idx] || "Observation initiale de la zone avant traitement."}</p>
                                            </div>
                                            <div className="description-item-premium solution">
                                                <h5><FaCheck /> Intervention</h5>
                                                <p>{apresTexts[idx] || "Application du procédé RoulezPropre pour une remise à neuf."}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: INFO SIDEBAR */}
                    <aside className="project-sidebar-premium">
                        <div className="info-card-premium">
                            <span className="badge-premium-rp">PRESTIGE</span>
                            <h1>{project.titre}</h1>

                            <div className="checklist-box-premium solution">
                                <h4><FaCheck /> Interventions réalisées</h4>
                                <ul className="checklist-list-premium">
                                    {apresTexts.length > 0 ? apresTexts.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    )) : <li>Nettoyage en profondeur</li>}
                                </ul>
                            </div>

                            <div className="checklist-box-premium problem">
                                <h4><FaTimes /> Problématiques</h4>
                                <ul className="checklist-list-premium">
                                    {avantTexts.length > 0 ? avantTexts.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    )) : <li>État initial à restaurer</li>}
                                </ul>
                            </div>

                            <Link to="/reservation" className="btn btn-primary btn-full mt-30">
                                Demander le même soin
                            </Link>
                        </div>
                    </aside>
                </div>

                {/* BOTTOM: SIMILAR PROJECTS */}
                {similarProjects.length > 0 && (
                    <div className="similar-section-premium">
                        <h2 className="section-title">Autres réalisations similaires</h2>
                        <div className="similar-grid-premium">
                            {similarProjects.map(p => (
                                <Link key={p.id} to={`/avant-apres/${p.id}`} className="similar-card-premium">
                                    <div className="card-img-premium">
                                        <img src={getFileUrl(p, (p.photo_apres && p.photo_apres.length > 0) ? p.photo_apres[0] : "")} alt={p.titre} />
                                    </div>
                                    <div className="card-body-premium">
                                        <h4>{p.titre}</h4>
                                        <span className="category-tag-premium">{p.categorie?.toUpperCase()}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* LIGHTBOX COMPONENT */}
            <AnimatePresence>
                {lightbox.isOpen && (
                    <Lightbox
                        images={lightbox.images}
                        currentIndex={lightbox.index}
                        onClose={() => setLightbox({ ...lightbox, isOpen: false })}
                        onPrev={() => setLightbox({ ...lightbox, index: (lightbox.index - 1 + lightbox.images.length) % lightbox.images.length })}
                        onNext={() => setLightbox({ ...lightbox, index: (lightbox.index + 1) % lightbox.images.length })}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default ProjectDetailsPage;
